import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Send } from 'lucide-react';
import { useToast } from '@kollez/design-system';

/** /error — direct port of kollez/pages/public/error.html. Three flavors via ?type=. */
type ErrorType = '404' | '403' | 'session';

interface ErrorSpec { code: string; path: string; title: string; body: string; actions: React.ReactNode; strike: boolean; }

export function ErrorPage() {
  const [params] = useSearchParams();
  const toast = useToast();
  const type = (params.get('type') as ErrorType) ?? '404';

  const spec = useMemo<ErrorSpec>(() => {
    if (type === '403') return {
      code: 'Error 403 · No access',
      path: 'Godavari Education Group › Reports › All colleges',
      title: "You don't have access to this.",
      body: "This page belongs to the group workspace. You're signed in to Godavari Institute of Technology as a placement officer. Ask your group admin for access, or switch workspace.",
      actions: (
        <>
          <Link className="btn btn-primary" to="/home">Back to home</Link>
          <button className="btn btn-outline" onClick={() => toast.show({ message: 'Request sent to Ravi Gujjula, Group Admin' })}><Send size={14} />Request access</button>
          <Link className="btn btn-ghost" to="/login">Switch workspace</Link>
        </>
      ),
      strike: false,
    };
    if (type === 'session') return {
      code: 'Signed out',
      path: 'Session ended at 14:32 IST',
      title: 'Your session has ended.',
      body: "For security we sign you out after 8 hours of inactivity. Sign in again and we'll bring you back to this page — nothing you saved is lost.",
      actions: (
        <>
          <Link className="btn btn-primary" to="/login">Sign in again</Link>
          <Link className="btn btn-ghost" to="/">Go to kollez.ai</Link>
        </>
      ),
      strike: false,
    };
    return {
      code: 'Error 404',
      path: '/tpo/drives/wipro-elite-2025',
      title: "This page isn't in the register.",
      body: 'The link may be old, or the drive was archived. Search for it, or head back to where you work.',
      actions: (
        <>
          <Link className="btn btn-primary" to="/home">Back to home</Link>
          <button className="btn btn-outline" onClick={() => toast.show({ message: 'Search palette coming soon.' })}><Search size={14} />Search kollez.ai</button>
        </>
      ),
      strike: true,
    };
  }, [type, toast]);

  return (
    <div className="flex flex-col px-6 py-8" style={{ minHeight: 'calc(100vh - 56px)' }}>
      <main className="mx-auto flex w-full flex-1 flex-col justify-center py-12" style={{ maxWidth: 640 }}>
        <div className="relative rounded-lg border border-line-tertiary bg-primary py-10" style={{ paddingLeft: 64, paddingRight: 32 }}>
          <div className="absolute bottom-0 top-0" style={{ left: 40, width: 2, background: 'rgb(var(--margin))' }} aria-hidden="true" />
          <p className="text-sm text-tertiary">{spec.code}</p>
          <p className="mt-3 font-medium text-secondary truncate">
            <span className={spec.strike ? 'line-through decoration-2' : ''}>{spec.path}</span>
          </p>
          <h1 className="h-page mt-6">{spec.title}</h1>
          <p className="subhead mt-3">{spec.body}</p>
          <div className="mt-8 flex flex-wrap gap-2">{spec.actions}</div>
        </div>
        <p className="mt-6 flex flex-wrap gap-2 text-xs text-tertiary">
          Preview:
          <Link className="link" to="/error?type=404">Not found</Link>
          <Link className="link" to="/error?type=403">No access</Link>
          <Link className="link" to="/error?type=session">Session expired</Link>
        </p>
      </main>
    </div>
  );
}
